import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Type, Palette, ArrowLeft, Save, Download, Copy, Check, Library, Layout, Component, MoveHorizontal, PanelLeftClose, Sun, Moon, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { designSystemService, type DesignSystem } from '@/lib/designSystemService';
import { TypographyEditor } from './typography/TypographyEditor';
import { PresetEditor } from './color-presets/PresetEditor';
import { SpacingEditor } from './spacing/SpacingEditor';
import { colorPresetService } from '@/lib/colorPresetService';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { ColorPreset } from './color-presets/types';
import { toast } from 'sonner';

interface DesignSystemEditorProps {
  isGlobalDarkMode?: boolean;
  setIsGlobalDarkMode?: (dark: boolean) => void;
  embedded?: boolean;
  headerContent?: React.ReactNode | ((props: { blueprintName?: string }) => React.ReactNode);
  dsIdOverride?: string | null;
  sidebarOpen?: boolean;
  setSidebarOpen?: (open: boolean) => void;
}

export const DesignSystemEditor: React.FC<DesignSystemEditorProps> = ({ 
  isGlobalDarkMode = false, 
  setIsGlobalDarkMode, 
  embedded = false, 
  headerContent, 
  dsIdOverride,
  sidebarOpen,
  setSidebarOpen
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();

  // Extract design system ID from URL: /brand/design-system/:id/... or /brand/blueprints/design-system/:id/...
  const pathParts = location.pathname.split('/');
  const dsIndex = pathParts.indexOf('design-system');
  const dsIdFromUrl = dsIndex !== -1 ? pathParts[dsIndex + 1] : undefined;
  const dsId = dsIdOverride || dsIdFromUrl;

  // Determine base path for navigation (supports blueprints, design-studio, and design-system)
  const isBlueprintRoute = location.pathname.includes('/blueprints/') || location.pathname.includes('/design-studio/');
  const dsBasePath = embedded && isBlueprintRoute
    ? `/brand/blueprints/${dsId}`
    : `/brand/design-system/${dsId}`;
  const backPath = isBlueprintRoute ? '/brand/blueprints' : '/brand/design-system';

  const [designSystem, setDesignSystem] = useState<DesignSystem | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localSidebarOpen, setLocalSidebarOpen] = useState(true);
  
  const isSidebarExpanded = sidebarOpen !== undefined ? sidebarOpen : localSidebarOpen;
  const toggleSidebar = () => {
    if (setSidebarOpen) setSidebarOpen(!isSidebarExpanded);
    else setLocalSidebarOpen(!isSidebarExpanded);
  };
  const [colorPreset, setColorPreset] = useState<ColorPreset | null>(null);
  const [colorLoading, setColorLoading] = useState(false);

  const [isExportOpen, setIsExportOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSavedState, setShowSavedState] = useState(true);

  // Save refs for child editors
  const colorSaveRef = useRef<(() => Promise<void>) | null>(null);
  const typographySaveRef = useRef<(() => Promise<void>) | null>(null);
  const colorCssRef = useRef<(() => string) | null>(null);
  const typographyCssRef = useRef<(() => string) | null>(null);

  const getActiveTab = () => {
    if (location.pathname.endsWith('/typography')) return 'typography';
    if (location.pathname.endsWith('/browse')) return 'browse';
    if (location.pathname.endsWith('/spacing')) return 'spacing';
    if (location.pathname.endsWith('/layouts')) return 'layouts';
    if (location.pathname.endsWith('/components')) return 'components';
    return 'colors';
  };
  const activeTab = getActiveTab();

  useEffect(() => {
    loadDesignSystem();
  }, [dsId]);

  useEffect(() => {
    if (designSystem?.color_preset_id && !colorPreset) {
      loadColorPreset();
    }
  }, [designSystem?.color_preset_id, colorPreset]);

  const loadDesignSystem = async () => {
    if (!dsId) return;
    try {
      setLoading(true);
      const ds = await designSystemService.getById(dsId);
      if (ds) {
        setDesignSystem(ds);
      } else {
        toast.error('Design system not found');
        navigate(backPath);
      }
    } catch (error) {
      console.error('Error loading design system:', error);
      toast.error('Failed to load design system');
      navigate(backPath);
    } finally {
      setLoading(false);
    }
  };

  const loadColorPreset = async () => {
    if (!designSystem?.color_preset_id) return;
    try {
      setColorLoading(true);
      const preset = await colorPresetService.getPresetById(designSystem.color_preset_id);
      if (preset) {
        setColorPreset(preset as unknown as ColorPreset);
      }
    } catch (error) {
      console.error('Error loading color preset:', error);
    } finally {
      setColorLoading(false);
    }
  };

  const handleBack = () => {
    navigate(backPath);
  };

  const handleColorSave = async (updatedPreset: ColorPreset): Promise<void> => {
    if (!currentWorkspace || !user || !designSystem) {
      toast.error('Missing context');
      throw new Error('Missing context');
    }

    if (designSystem.color_preset_id) {
      await colorPresetService.updatePreset(designSystem.color_preset_id, {
        name: updatedPreset.name,
        colors: updatedPreset.colors,
      });
      toast.success('Color preset updated');
    }
  };

  const handleColorCancel = () => {
    navigate(`${dsBasePath}/colors`);
  };

  // Unified save that triggers save on both editors
  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const promises: Promise<void>[] = [];
      if (colorSaveRef.current) promises.push(colorSaveRef.current());
      if (typographySaveRef.current) promises.push(typographySaveRef.current());
      if (designSystem) {
        promises.push(designSystemService.update(designSystem.id, { name: designSystem.name }).then(() => {}));
      }
      await Promise.all(promises);
      toast.success('Design system saved');
      setShowSavedState(true);
    } catch (error) {
      console.error('Error saving design system:', error);
      toast.error('Failed to save design system');
    } finally {
      setSaving(false);
    }
  };

  const getCombinedCss = () => {
    let css = `/* Design System: ${designSystem?.name || 'Untitled'} */\n`;
    css += `:root {\n`;

    if (colorCssRef.current) {
      css += `\n${colorCssRef.current()}`;
    }

    if (typographyCssRef.current) {
      css += `\n${typographyCssRef.current()}`;
    }

    css += `}\n`;
    return css;
  };

  const handleCopyCss = async () => {
    try {
      await navigator.clipboard.writeText(getCombinedCss());
      setCopied(true);
      toast.success('CSS copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy CSS');
    }
  };

  const handleDownloadCss = () => {
    const css = getCombinedCss();
    const blob = new Blob([css], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(designSystem?.name || 'design-system').toLowerCase().replace(/\s+/g, '-')}.css`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('CSS file downloaded');
  };

  useEffect(() => {
    const onSave = () => handleSaveAll();
    const onExportCss = () => handleDownloadCss();

    window.addEventListener('design-studio-save', onSave);
    window.addEventListener('design-studio-export-css', onExportCss);

    return () => {
      window.removeEventListener('design-studio-save', onSave);
      window.removeEventListener('design-studio-export-css', onExportCss);
    };
  }, [designSystem]);

  const tabs = [
    { id: 'browse', label: 'Templates', icon: Library, path: `${dsBasePath}/browse` },
    { id: 'colors', label: 'Colors', icon: Palette, path: `${dsBasePath}/colors` },
    { id: 'typography', label: 'Typography', icon: Type, path: `${dsBasePath}/typography` },
    { id: 'spacing', label: 'Spacing', icon: MoveHorizontal, path: `${dsBasePath}/spacing` },
    { id: 'layouts', label: 'Layouts', icon: Layout, path: `${dsBasePath}/layouts` },
    { id: 'components', label: 'Components', icon: Component, path: `${dsBasePath}/components` },
  ];

  if (loading) {
    return null;
  }

  if (!designSystem) {
    return null;
  }

  return (
    <div className="w-full h-screen flex flex-col overflow-hidden relative">
      <Helmet>
        <title>{designSystem.name} — Design System | AgencyPlus</title>
      </Helmet>



      {/* Main Header (Hide if embedded) */}
      {!embedded && (
        <div className="px-6 py-4 border-b border-border bg-card/30 backdrop-blur-sm sticky top-0 z-20 flex items-center justify-between">
        <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <input
              type="text"
              value={designSystem?.name || ''}
              onChange={(e) => {
                setShowSavedState(false);
                setDesignSystem(prev => prev ? { ...prev, name: e.target.value } : prev);
              }}
              placeholder="Design System Name"
              className="font-semibold text-lg bg-transparent border-0 outline-none focus:ring-0 p-0 w-64 text-foreground placeholder:text-muted-foreground truncate hover:bg-muted/50 focus:bg-muted/50 rounded transition-colors"
            />
        </div>
        <div className="flex items-center gap-4 bg-card border border-border rounded-full px-5 py-2 shadow-sm">
           {/* Save Button / Status */}
           <button 
             onClick={() => {
               setShowSavedState(false);
               handleSaveAll();
             }}
             disabled={saving}
             className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
               showSavedState && !saving
                 ? 'text-emerald-600 hover:text-emerald-700' 
                 : 'text-foreground hover:text-foreground/80'
             } disabled:opacity-50`}
           >
             {saving ? (
               <Loader2 className="w-4 h-4 animate-spin" />
             ) : showSavedState ? (
               <Check className="w-4 h-4" />
             ) : (
               <Save className="w-4 h-4" />
             )}
             {saving ? 'Saving...' : showSavedState ? 'Saved' : 'Save'}
           </button>

           {/* Dark mode toggle */}
           <div className="flex items-center gap-2 text-muted-foreground ml-2">
             <Sun className="w-4 h-4" />
             <Switch 
               checked={isGlobalDarkMode} 
               onCheckedChange={setIsGlobalDarkMode}
             />
             <Moon className="w-4 h-4" />
           </div>

           <div className="w-px h-4 bg-border" />

           {/* Export */}
           <button 
             onClick={() => setIsExportOpen(true)}
             className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
           >
             <Download className="w-4 h-4" /> Export
           </button>
        </div>
      </div>
      )}

      <div className={`flex-1 flex overflow-hidden`}>
        
        {/* Unified Sidebar Container */}
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden flex flex-col border-r border-border bg-sidebar ${
            isSidebarExpanded ? 'w-[480px] min-w-[480px]' : 'w-0 min-w-0'
          }`}
        >
          {/* Sidebar Header (Matches Sitemap) */}
          <div className="px-3 py-3 border-b border-sidebar-border flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <button
                onClick={handleBack}
                className="p-2 rounded-lg text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
                title="Back"
              >
                <ArrowLeft size={18} />
              </button>
              <span className="text-sm font-semibold text-sidebar-foreground tracking-tight truncate max-w-[240px]" title={designSystem.name}>
                {designSystem.name}
              </span>
            </div>
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
              title="Collapse sidebar"
            >
              <PanelLeftClose size={18} />
            </button>
          </div>

          {/* Inner Sidebar Content */}
          <div className="flex-1 flex overflow-hidden">
            {/* Vertical navigation icons */}
            <div className="w-20 border-r border-border bg-background flex flex-col z-10 shrink-0">
              <div className="flex-1 overflow-y-auto py-3 space-y-2">
                  {tabs.map((tab) => {
                      const isActive = activeTab === tab.id;
                      return (
                        <div key={tab.id} className="px-2.5">
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => navigate(tab.path)}
                                className={`w-10 h-10 mx-auto flex items-center justify-center rounded-full transition-all duration-200 ${isActive
                                  ? isGlobalDarkMode
                                    ? 'bg-nav-active text-white shadow-lg'
                                    : 'bg-sidebar-light-active text-gray-900 shadow-sm'
                                  : isGlobalDarkMode
                                    ? 'text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-sidebar-light-hover'
                                  }`}
                              >
                                <tab.icon className="w-5 h-5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="font-medium">
                              {tab.label}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      );
                  })}
              </div>
            </div>

            {/* Editor Panel */}
            <div className="flex-1 flex flex-col overflow-hidden bg-background">
        {designSystem.typography_preset_id && (
          <div 
            className="flex-1 flex flex-col overflow-hidden" 
            style={{ display: activeTab === 'typography' ? 'flex' : 'none' }}
          >
            <TypographyEditor
              presetId={designSystem.typography_preset_id}
              onBack={handleBack}
              embedded
              saveRef={typographySaveRef}
              getCssRef={typographyCssRef}
              onChange={() => setShowSavedState(false)}
            />
          </div>
        )}

        <div 
          className="flex-1 flex flex-col overflow-hidden"
          style={{ display: (activeTab === 'colors' || activeTab === 'browse') ? 'flex' : 'none' }}
        >
          {colorLoading ? (
            null
          ) : (
            <PresetEditor
              preset={colorPreset}
              onSave={handleColorSave}
              onCancel={handleColorCancel}
              embedded
              saveRef={colorSaveRef}
              getCssRef={colorCssRef}
              defaultView={activeTab === 'browse' ? 'browse' : 'editor'}
              onTemplateApplied={() => navigate(`${dsBasePath}/colors`)}
              onChange={() => setShowSavedState(false)}
            />
          )}
        </div>

        {/* Spacing Editor Content */}
        <div 
          className="flex-1 flex flex-col overflow-hidden"
          style={{ display: activeTab === 'spacing' ? 'flex' : 'none' }}
        >
          <SpacingEditor />
        </div>

        {/* Blank tab content for Layouts */}
        <div 
          className="flex-1 flex flex-col items-center justify-center p-8 text-center"
          style={{ display: activeTab === 'layouts' ? 'flex' : 'none' }}
        >
          <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
            <Layout className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Layouts Editor</h2>
          <p className="text-muted-foreground max-w-md">
            Design and configure standard layout structures, grids, and spacing systems. Coming soon.
          </p>
        </div>

        {/* Blank tab content for Components */}
        <div 
          className="flex-1 flex flex-col items-center justify-center p-8 text-center"
          style={{ display: activeTab === 'components' ? 'flex' : 'none' }}
        >
          <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
            <Component className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Component Library</h2>
          <p className="text-muted-foreground max-w-md">
            Customize and preview core UI components like buttons, inputs, and cards. Coming soon.
          </p>
        </div>
            </div>
          </div>
        </div>

      {/* Right: Website Preview Panel — flex-1 */}
      <div className={`flex-1 relative flex flex-col overflow-hidden transition-colors duration-300 ${isGlobalDarkMode ? 'bg-zinc-950 text-white border-zinc-800' : 'bg-muted/20'}`}>
        
        {/* Floating Header */}
        {headerContent && (
          <div className="absolute top-4 left-4 right-4 z-50 pointer-events-none">
            <div className="pointer-events-auto">
              {typeof headerContent === 'function' ? headerContent({ blueprintName: designSystem.name }) : headerContent}
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${isGlobalDarkMode ? 'bg-zinc-900' : 'bg-muted/60'}`}>
            <svg className={`w-10 h-10 ${isGlobalDarkMode ? 'text-white/20' : 'text-muted-foreground/40'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18" />
              <path d="M9 21V9" />
            </svg>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-muted-foreground/60">Website Preview</p>
            <p className="text-xs text-muted-foreground/40 mt-1">Live preview coming soon</p>
          </div>
        </div>
      </div>
      </div>


      <Dialog open={isExportOpen} onOpenChange={setIsExportOpen}>
        <DialogContent className="max-w-2xl bg-card border-border/50">
          <DialogHeader>
            <DialogTitle>Export Design System CSS</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground mt-2">
              This CSS contains both your Color Palette and Typography Scale variables. Add this to your root stylesheet or inject it into your global theme.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <pre className="p-4 rounded-xl bg-muted overflow-auto max-h-[50vh] text-xs font-mono text-foreground border border-border/50">
                <code>{isExportOpen ? getCombinedCss() : ''}</code>
              </pre>
              <button
                onClick={handleCopyCss}
                className="absolute top-2 right-2 p-2 rounded-lg bg-background/50 hover:bg-background/80 text-muted-foreground transition-all backdrop-blur-sm border border-border/50"
                title="Copy to clipboard"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex justify-end pt-4 border-t border-border/50">
              <button
                onClick={handleDownloadCss}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-workspace-accent hover:bg-workspace-accent/90 text-white text-sm font-semibold transition-all shadow-md"
              >
                <Download className="w-4 h-4" />
                Download .css
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
